import React, { useEffect, useState } from "react";
import LayoutHOC from "../LayoutHOC";
import { useParams, Link, useNavigate } from "react-router-dom";
import useCandidateDetails from "../../hooks/useCandidateDetails";
import { DELETE_CANDIDATE, PROFILE_PIC_URL, GET_CANDIDATE_APPLIED_JOBS } from "../../utils/constants";
import { useSelector } from "react-redux";

const CandidateDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const details = useCandidateDetails(id);
  const user = useSelector((state) => state.user);
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);

  useEffect(() => {
    const fetchAppliedJobs = async () => {
      if (!user || !id) return;
      try {
        const response = await fetch(`${GET_CANDIDATE_APPLIED_JOBS}${id}`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAppliedJobs(data.data || []);
        }
      } catch (error) {
        console.error("Error fetching applied jobs:", error);
      } finally {
        setLoadingJobs(false);
      }
    };
    fetchAppliedJobs();
  }, [user, id]);

  const Skills = details?.data?.skills;
  const Languages = details?.data?.languages;
  const profilePicUrl =  details?.data?.profilepic;
  const resume = PROFILE_PIC_URL + details?.data?.resume;

  const handleDelete = async () => {
    const confirmDelete = window.confirm("Are you sure you want to delete this candidate?");
    if (confirmDelete) {
      try {
        const { token } = user;
        const response = await fetch(`${DELETE_CANDIDATE}${id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          alert("Candidate deleted successfully!");
          navigate("/candidate");
        } else {
          alert("Failed to delete candidate. Please try again.");
        }
      } catch (error) {
        console.error("Error deleting candidate:", error);
        alert("Failed to delete candidate. Please try again.");
      }
    }
  };

  const InfoCard = ({ icon, label, value, iconColor = "text-teal-500" }) => (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg bg-gray-50 ${iconColor}`}>
          <i className={`bi ${icon} text-lg`}></i>
        </div>
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
          <p className="font-medium text-gray-900 mt-1">{value || 'N/A'}</p>
        </div>
      </div>
    </div>
  );

  const StatusBadge = ({ verified, label }) => (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-2">
        <i className={`bi ${verified ? 'bi-check-circle-fill text-emerald-500' : 'bi-clock text-amber-500'}`}></i>
        <span className="text-gray-700">{label}</span>
      </div>
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
        verified 
          ? 'bg-emerald-100 text-emerald-700' 
          : 'bg-amber-100 text-amber-700'
      }`}>
        {verified ? 'Verified' : 'Pending'}
      </span>
    </div>
  );

  const DocumentItem = ({ icon, label, url, status, iconBg = "bg-blue-100", iconColor = "text-blue-500" }) => (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-3">
        <div className={`p-2 ${iconBg} rounded-lg`}>
          <i className={`bi ${icon} ${iconColor}`}></i>
        </div>
        <div>
          <p className="font-medium text-gray-900 text-sm">{label}</p>
          {url ? (
            <a 
              href={PROFILE_PIC_URL + url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-teal-600 hover:underline"
            >
              View Document
            </a>
          ) : (
            <span className="text-xs text-gray-400">Not uploaded</span>
          )}
        </div>
      </div>
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
        status === 'verified' || status === 'approved'
          ? 'bg-emerald-100 text-emerald-700'
          : 'bg-red-100 text-red-700'
      }`}>
        {status === 'verified' || status === 'approved' ? 'Verified' : 'Not Verified'}
      </span>
    </div>
  );

  const getStatusConfig = (status) => {
    const statusLower = (status || 'submitted').toLowerCase();
    const configs = {
      submitted: { bg: 'bg-blue-100', text: 'text-blue-700', icon: 'bi-send-fill', label: 'Submitted' },
      shortlist: { bg: 'bg-purple-100', text: 'text-purple-700', icon: 'bi-star-fill', label: 'Shortlist' },
      shortlisted: { bg: 'bg-purple-100', text: 'text-purple-700', icon: 'bi-star-fill', label: 'Shortlist' },
      interview: { bg: 'bg-amber-100', text: 'text-amber-700', icon: 'bi-calendar-event-fill', label: 'Interview' },
      offer: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: 'bi-gift-fill', label: 'Offer' },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', icon: 'bi-x-circle-fill', label: 'Rejected' }
    };
    return configs[statusLower] || configs.submitted;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatSalary = (min, max, type) => {
    if (!min && !max) return 'Not specified';
    const salaryType = type || 'per month';
    if (min && max) return `${min.toLocaleString()} - ${max.toLocaleString()} ${salaryType}`;
    if (min) return `${min.toLocaleString()}+ ${salaryType}`;
    return `Up to ${max.toLocaleString()} ${salaryType}`;
  };

  return (
    <main id="maincontent">
      <div className="p-6 mt-14 min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/candidate"
              className="p-2 rounded-lg bg-white shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors"
            >
              <i className="bi bi-arrow-left text-gray-600"></i>
            </Link>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-teal-800 to-cyan-600 bg-clip-text text-transparent">
                Candidate Profile
              </h1>
              <p className="text-gray-500 text-sm">View detailed candidate information</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`/edit_candidate/${id}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors font-medium"
            >
              <i className="bi bi-pencil"></i>
              Edit
            </Link>
            <button
              onClick={handleDelete}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors font-medium"
            >
              <i className="bi bi-trash"></i>
              Delete
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-teal-500 to-cyan-600 h-32"></div>
          <div className="px-6 pb-6">
            <div className="flex flex-col md:flex-row gap-6 -mt-16">
              <div className="w-32 h-32 rounded-2xl bg-white p-1 shadow-lg">
                {details?.data?.profilepic ? (
                  <img src={profilePicUrl} alt="Profile" className="w-full h-full object-cover rounded-xl" />
                ) : (
                  <div className="w-full h-full rounded-xl bg-gradient-to-br from-teal-100 to-cyan-100 flex items-center justify-center">
                    <i className="bi bi-person text-4xl text-teal-500"></i>
                  </div>
                )}
              </div>
              <div className="flex-1 pt-4 md:pt-16">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{details?.data?.user_name || 'Unknown'}</h2>
                    <p className="text-teal-600 font-medium">{details?.data?.jobposition || 'Position not specified'}</p>
                    <p className="text-gray-500 text-sm mt-1">{details?.data?.email}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
                      details?.data?.status == 1
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      <span className={`w-2 h-2 rounded-full mr-2 ${
                        details?.data?.status == 1 ? 'bg-emerald-500' : 'bg-gray-400'
                      }`}></span>
                      {details?.data?.status == 1 ? 'Active' : 'Inactive'}
                    </span>
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
                      details?.data?.otp_verified === 'activated' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      <i className={`bi ${details?.data?.otp_verified === 'activated' ? 'bi-phone-fill' : 'bi-phone'} mr-1.5`}></i>
                      OTP {details?.data?.otp_verified === 'activated' ? 'Verified' : 'Pending'}
                    </span>
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
                      details?.data?.profile_verified === 'activated' 
                        ? 'bg-teal-100 text-teal-700' 
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      <i className={`bi ${details?.data?.profile_verified === 'activated' ? 'bi-patch-check-fill' : 'bi-patch-check'} mr-1.5`}></i>
                      Profile {details?.data?.profile_verified === 'activated' ? 'Verified' : 'Pending'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <i className="bi bi-person-lines-fill text-teal-500"></i>
                Personal Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <InfoCard icon="bi-briefcase" label="Profession" value={details?.data?.jobposition} />
                <InfoCard icon="bi-award" label="Experience" value={details?.data?.experience} />
                <InfoCard icon="bi-mortarboard" label="Education" value={details?.data?.education} />
                <InfoCard icon="bi-gender-ambiguous" label="Gender" value={details?.data?.gender} />
                <InfoCard icon="bi-heart" label="Marital Status" value={details?.data?.maritalStatus} />
                <InfoCard icon="bi-calendar3" label="Date of Birth" value={details?.data?.dob} />
                <InfoCard icon="bi-telephone" label="Mobile" value={details?.data?.phone} />
                <InfoCard icon="bi-geo-alt" label="Address" value={details?.data?.address} />
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <i className="bi bi-file-text text-teal-500"></i>
                About
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {details?.data?.description || 'No description available.'}
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <i className="bi bi-shield-check text-teal-500"></i>
                Verification Status
              </h3>
              <div className="space-y-3">
                <StatusBadge 
                  verified={details?.data?.otp_verified === 'activated'} 
                  label="OTP Verification" 
                />
                <StatusBadge 
                  verified={details?.data?.profile_verified === 'activated'} 
                  label="Profile Verification" 
                />
                <StatusBadge 
                  verified={details?.data?.status ==='1'} 
                  label="Account Status" 
                />
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <i className="bi bi-folder2-open text-teal-500"></i>
                Documents
              </h3>
              <div className="space-y-3">
                <DocumentItem 
                  icon="bi-file-earmark-pdf-fill" 
                  label="Resume / CV" 
                  url={details?.data?.resume}
                  status={details?.data?.resume ? 'verified' : null}
                  iconBg="bg-red-100"
                  iconColor="text-red-500"
                />
               
                <DocumentItem 
                  icon="bi-file-earmark-text" 
                  label="Aadhar Card" 
                  status={details?.data?.aadharCardNumber ? 'verified' : null}
                  iconBg="bg-blue-100"
                  iconColor="text-blue-500"
                  showLink={false}
                />
                <DocumentItem 
                  icon="bi-file-earmark-text" 
                  label="PAN Card" 
                  status={details?.data?.panCardNumber ? 'verified' : null}
                  iconBg="bg-blue-100"
                  iconColor="text-blue-500"
                  showLink={false}
                />

                <DocumentItem 
                  icon="bi-file-earmark-image" 
                  label="Profile Photo" 
                  url={details?.data?.profilepic}
                  status={details?.data?.profilepic ? 'verified' : null}
                  iconBg="bg-teal-100"
                  iconColor="text-teal-500"
                />
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <i className="bi bi-tools text-teal-500"></i>
                Skills
              </h3>
              <div className="flex flex-wrap gap-2">
                {Skills && Skills.length > 0 ? (
                  Skills.map((skill, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-teal-50 text-teal-700 border border-teal-100"
                    >
                      {skill.name}
                    </span>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm">No skills listed</p>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <i className="bi bi-translate text-teal-500"></i>
                Languages
              </h3>
              <div className="flex flex-wrap gap-2">
                {Languages && Languages.length > 0 ? (
                  Languages.map((lang, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-cyan-50 text-cyan-700 border border-cyan-100"
                    >
                      {lang.name}
                    </span>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm">No languages listed</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mt-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <i className="bi bi-briefcase-fill text-teal-500"></i>
              Applied Jobs
            </h3>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-teal-100 text-teal-700">
              {appliedJobs.length} Application{appliedJobs.length !== 1 ? 's' : ''}
            </span>
          </div>

          {loadingJobs ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
            </div>
          ) : appliedJobs.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="bi bi-briefcase text-2xl text-gray-400"></i>
              </div>
              <h4 className="text-gray-600 font-medium mb-1">No Applications Yet</h4>
              <p className="text-gray-400 text-sm">This candidate hasn't applied to any jobs</p>
            </div>
          ) : (
            <div className="space-y-4">
              {appliedJobs.map((job) => {
                const statusConfig = getStatusConfig(job.status);
                return (
                  <div 
                    key={job.id} 
                    className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors gap-4"
                  >
                    <div className="flex-1">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="bi bi-building text-white"></i>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">
                            {job.job_title || job.jobPosition || 'Job Position'}
                          </h4>
                          <p className="text-sm text-gray-600">{job.company_name || 'Unknown Company'}</p>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <i className="bi bi-cash"></i>
                              {formatSalary(job.minSalary, job.maxSalary, job.salary_type)}
                            </span>
                            <span className="flex items-center gap-1">
                              <i className="bi bi-calendar3"></i>
                              Applied: {formatDate(job.applied_date)}
                            </span>
                            {job.deadline && (
                              <span className="flex items-center gap-1">
                                <i className="bi bi-clock-history"></i>
                                Deadline: {formatDate(job.deadline)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 md:flex-shrink-0">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${statusConfig.bg} ${statusConfig.text}`}>
                        <i className={`bi ${statusConfig.icon}`}></i>
                        {statusConfig.label}
                      </span>
                      {job.job_id && (
                        <Link 
                          to={`/job_details/${job.job_id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                        >
                          <i className="bi bi-eye"></i>
                          View Job
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default LayoutHOC(CandidateDetails);
